<template>
  <Modal :open="isModalOpen">
    <div class="mt-3 text-center sm:mt-5">
      <h3 class="text-xl leading-6 font-semibold text-gray-600">
        Zahlung per Lastschrift:
      </h3>
      <h3 class="text-xl leading-6 font-semibold text-green-400 pt-4"><span class="pr-1"></span> {{
        tenant.yearlyRateString }} im
        Jahr
      </h3>
      <p class="text-sm leading-6 font-regular text-gray-600"><span class="pr-1"></span>
        Das entspricht {{ tenant.monthlyRateString }} monatlich.
      </p>
    </div>
    <div class="my-4 space-y-4">
      <div class="flex sm:col-span-6 text-green-400 font-medium">
        <label class="block text-sm font-medium text-gray-700" for="next_IBAN">IBAN</label>
        <hr class="flex-1 mt-3 mx-2" />
        <i class="fas fa-question-circle mt-1 cursor-pointer text-gray-400" @click="showInfo = true"></i>
      </div>
      <div v-if="showInfo" class="px-3 pb-3 pt-1 w-full bg-green-50 text-green-500 text-xs round‚ed-md">
        <div class="flex justify-end cursor-pointer">
          <i class="fal fa-times text-lg" @click="showInfo = false"></i>
        </div>
        <div class="font-semibold mb-3">
          IBAN
        </div>
        <div class="font-light">
          <p class="pb-2">Wir können Ihnen derzeit leider nur eine jährliche Zahlung anbieten. Wir arbeiten mit
            Hochdruck daran,
            Ihnen in Zukunft auch weitere Zahlungsoptionen zur Verfügung zu stellen.</p>
          <p class="pb-2">Im Falle einer Kündigung berechnen wir Ihnen nur die Tage bis zu diesem Zeitpunkt.</p>
          <p class="pb-4">Sollten Sie zum Zeitpunkt der Kündigung den jährlichen Beitrag bereits gezahlt haben,
            erstatten wir Ihnen
            diesen anteilig auf den Tag genau zurück.</p>
          <p>Die IBAN (International Bank Account Number) dient zur Identifizierung von Bankverbindungen.
            Sie finden diese auf Ihrer girocard. Sollte die IBAN dort nicht aufgeführt sein, können Sie
            diese bei Ihrer Bank erfragen oder online errechnen lassen.</p>
        </div>
      </div>
      <InputLabeled id="next_IBAN" :show-label="false" placeholder="DE00 0000 0000 0000 0000 00" />
    </div>
    <div class="hidden my-4 space-y-4 text-gray-500 font-light" data-iban-target="banknameContainer">
      <label class="mr-1 text-sm" for="next_IBAN">BANK:</label>
      <span data-iban-target="bankname"></span>
    </div>

    <div class="mb-4 relative flex items-start">
      <div class="flex items-center h-5">
        <input type="checkbox" id="next_sepaTerms" name="next_sepaTerms" v-model="modelValue"
          class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-400 rounded">
      </div>
      <div class="ml-3 text-sm">
        <label for="next_sepaTerms" class="font-medium text-gray-700">SEPA-MANDAT</label>
        <p class="text-gray-500 font-light text-xs">
          Ich ermächtige die Alteos GmbH, Zahlungen von meinem Konto mittels Lastschrift einzuziehen.
          Zugleich weise ich mein Kreditinstitut an, die von der Alteos GmbH auf mein Konto gezogenen
          Lastschriften einzulösen. Hinweis: Ich kann innerhalb von acht Wochen, beginnend mit dem
          Belastungsdatum, die Erstattung des belasteten Betrages verlangen. Es gelten dabei die mit
          meinem Kreditinstitut vereinbarten Bedingungen.
        </p>
      </div>
    </div>
    <p v-if="errors['next_sepaTerms']" class="mt-2 text-sm text-red-600 error-message">{{ errors['next_sepaTerms'] }}</p>
    <hr>
    <div class="mt-4 sm:flex justify-center">
      <button type="button" @click="submitForm" :disabled="isLoading"
        class="flex items-center justify-center px-4 py-1 border border-transparent font-normal rounded-3xl shadow-sm text-white bg-green-400 hover:bg-green-300 sm:px-8 w-full sm:w-auto">
        Kostenpflichtig abschliessen
        <svg v-show="isLoading" class="animate-spin ml-4" width="24" height="24" viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg">
          <path class="stroke-green-900 fill-green-900"
            d="M10.72,19.9a8,8,0,0,1-6.5-9.79A7.77,7.77,0,0,1,10.4,4.16a8,8,0,0,1,9.49,6.52A1.54,1.54,0,0,0,21.38,12h.13a1.37,1.37,0,0,0,1.38-1.54,11,11,0,1,0-12.7,12.39A1.54,1.54,0,0,0,12,21.34h0A1.47,1.47,0,0,0,10.72,19.9Z" />
        </svg>
      </button>
    </div>
    <div class="mt-4 justify-center">
      <p class="text-gray-500 font-light text-xs">
        Durch Klicken auf "Kostenpflichtig abschließen" werden Ihre Daten an uns übermittelt und Sie
        schließen einen rechtsverbindlichen und kostenpflichtigen Versicherungsvertrag ab. Sie bestätigen die
        <span class="text-green-400 underline">
          <a href="/files/vertragsunterlagen_heysafe.pdf" target="_blank">
            Versicherungsbedingungen und Vertragsunterlagen
          </a>
        </span>
        zur Kenntnis genommen, heruntergeladen und gespeichert zu haben.
        Die Vertragsunterlagen erhalten Sie zusammen mit Ihrem Versicherungsschein im Anschluss per E-Mail.
        Ab diesem Zeitpunkt steht Ihnen ein 14-tägiges Widerrufsrecht zu. Der erste Beitrag wird mit
        dem Versand der Bürgschaftsurkunde, unabhängig vom Datum des Einzugs in die Wohnung, eingezogen.
      </p>
    </div>
  </Modal>
</template>

<script setup>
import { watch, computed, ref } from "vue";
import InputLabeled from "./InputLabeled";
import { useTenantStore } from "../stores/tenant";
import Modal from "../../common/components/Modal";
import emitter from "../../common/utils/eventbus";

const tenant = useTenantStore();
const isModalOpen = computed(() => tenant.processing.isIbanModalOpen);

// Computed Property, die direkt mit der Checkbox verbunden ist
const modelValue = computed({
  get: () => tenant.formValues['next_sepaTerms'] === '1', // Hole den Wert aus dem Store
  set: (newValue) => {
    tenant.setFormValue('next_sepaTerms', newValue ? '1' : ''); // Aktualisiere den Wert im Store
  }
});

const errors = computed(() => tenant.errors);

watch(modelValue, async (newValue) => {
  await tenant.validateField('next_sepaTerms', newValue ? '1' : '');
});

async function submitForm() {
  const isValid = await tenant.validateSection('payment');
  if (!isValid) {
    return;
  };

  emitter.emit('submit-form');
}
const isLoading = ref(false);
const showInfo = ref(false);

</script>
